import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableLink, FormattedNumber } from '@/components/global';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';

interface GoodsReceipt {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  totalItems: number;
  totalQuantity: number;
  warehouse?: {
    id: string;
    code: string;
    name: string;
  };
  supplier?: {
    id: string;
    code: string;
    name: string;
  };
}

interface PaginatedResult {
  items: GoodsReceipt[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_OPTIONS = ['ALL', 'PENDING', 'COMPLETED', 'CANCELLED'];

const statusStyles: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

export default function GoodsReceiptsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'ALL');
  const [loading, setLoading] = useState(true);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'ALL') {
        params.set('status', statusFilter);
      }
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/goods-receipts?${params}`);
      if (res.ok) {
        const data: PaginatedResult = await res.json();
        setReceipts(data.items);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error loading goods receipts:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [statusFilter, page, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold">Goods Receipts</h1>
          <Button onClick={() => navigate('/inventory/goods-receipts/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Receipt
          </Button>
        </div>

        <Card className="mb-6">
          <div className="p-4 flex gap-4">
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status === 'ALL' ? 'All Statuses' : status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receipt #</TableHead>
                <TableHead className="hidden sm:table-cell">Date</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="hidden md:table-cell">Warehouse</TableHead>
                <TableHead className="hidden text-right lg:table-cell">Items</TableHead>
                <TableHead className="hidden text-right md:table-cell">Quantity</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : receipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No goods receipts found
                  </TableCell>
                </TableRow>
              ) : (
                receipts.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <TableLink to={`/inventory/goods-receipts/${r.id}`}>
                        {r.receiptNumber}
                      </TableLink>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{new Date(r.receiptDate).toLocaleDateString()}</TableCell>
                    <TableCell>{r.supplier?.name || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{r.warehouse?.name || '-'}</TableCell>
                    <TableCell className="hidden text-right lg:table-cell">
                      <FormattedNumber value={r.totalItems} />
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">
                      <FormattedNumber value={r.totalQuantity} />
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyles[r.status] || 'bg-muted'}`}>
                        {r.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} • {total} total
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
